import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'

const DEBUFFS_DATA = [
  {
    id: 'scrolling',
    name: 'Scrolling Infinito',
    subtitle: 'Dopamina Glitch',
    desc: 'Usi lo smartphone nei primi 60 minuti della giornata saturando i ricettori.',
    penalty: { focus: 45, hp: 15, stamina: 10 },
    icon: '📱',
    color: '#ef4444'
  },
  {
    id: 'paralisi',
    name: 'Paralisi da Analisi',
    subtitle: 'Ambiguità Nebbia',
    desc: 'Sai cosa fare ma pensi a quanto sarà difficile, bloccandoti prima di cominciare.',
    penalty: { focus: 20, hp: 10, stamina: 50 },
    icon: '🧠',
    color: '#facc15'
  },
  {
    id: 'standby',
    name: 'Standby Fisico',
    subtitle: '1 HP Avatar',
    desc: 'Sedentarietà prolungata, sonno sregolato e postura curva da scrivania.',
    penalty: { focus: 15, hp: 50, stamina: 25 },
    icon: '🛋️',
    color: '#00d4ff'
  },
  {
    id: 'chierico',
    name: 'Sindrome del Chierico',
    subtitle: 'Priorità Party',
    desc: 'Risolvi i problemi e i task di tutti gli altri, lasciando le tue quest personali all\'ultimo posto.',
    penalty: { focus: 15, hp: 20, stamina: 30 },
    icon: '🛡️',
    color: '#8b5cf6'
  }
]

export default function AvatarDebuffAnalyzer() {
  const [selectedDebuffs, setSelectedDebuffs] = useState(['scrolling']) // Default start with scrolling active

  const toggleDebuff = (id) => {
    setSelectedDebuffs(prev => 
      prev.includes(id) ? prev.filter(d => d !== id) : [...prev, id]
    )
  }

  // Statistiche base dell'Avatar sano
  const baseStats = { hp: 95, focus: 90, stamina: 85 }

  // Calcolo detrazioni statistiche basate sui debuff attivi
  let hpPenalty = 0
  let focusPenalty = 0
  let staminaPenalty = 0

  selectedDebuffs.forEach(debuffId => {
    const data = DEBUFFS_DATA.find(d => d.id === debuffId)
    if (data) {
      hpPenalty += data.penalty.hp
      focusPenalty += data.penalty.focus
      staminaPenalty += data.penalty.stamina
    }
  })

  const currentHp = Math.max(5, baseStats.hp - hpPenalty)
  const currentFocus = Math.max(5, baseStats.focus - focusPenalty)
  const currentStamina = Math.max(5, baseStats.stamina - staminaPenalty)

  // Calcolo dei consigli dinamici
  const getDynamicAdvice = () => {
    if (selectedDebuffs.length === 0) {
      return {
        text: 'Il tuo Avatar è completamente purificato! Hai il 100% dell\'efficienza per affrontare le quest del mondo reale. Mantieni questo focus!',
        badge: 'Status: Eroe Leggendario',
        color: '#10b981',
        quest: 'Mantieni i tuoi buff attivi'
      }
    }
    
    if (selectedDebuffs.includes('scrolling') && selectedDebuffs.includes('standby') && selectedDebuffs.length >= 3) {
      return {
        text: 'Rilevato loop dopaminergico critico e sonnolenza corporea. Il tuo avatar ha pochissimi HP. La tua priorità assoluta è disattivare lo smartphone la prima ora ed eseguire 5 min di respirazione.',
        badge: 'Stato Critico: 1 HP Left',
        color: '#ef4444',
        quest: 'Attiva la Safe Zone (Lv. 01)'
      }
    }

    if (selectedDebuffs.includes('paralisi')) {
      return {
        text: 'La nebbia dell\'ambiguità ti sta bloccando. Non pianificare tutto. Scomponi il tuo prossimo compito in sotto-quest da 5 minuti ciascuna.',
        badge: 'Consiglio: Abbatti l\'Ambiguità',
        color: '#facc15',
        quest: 'Sblocco della Classe (Lv. 10)'
      }
    }

    if (selectedDebuffs.includes('scrolling')) {
      return {
        text: 'Il glitch dopaminergico sta consumando il tuo Mana. Disattiva lo scrolling mattutino per proteggere il tuo focus per le attività importanti.',
        badge: 'Consiglio: Dopamine Cleanse',
        color: '#ef4444',
        quest: 'Pulisci i Canali Neurali'
      }
    }

    if (selectedDebuffs.includes('standby')) {
      return {
        text: 'Il tuo corpo è in modalità risparmio energetico. L\'avatar reale ha bisogno di risvegliare la stamina. Fai 10 squat o alzati dalla sedia adesso.',
        badge: 'Consiglio: Risveglio Stamina',
        color: '#00d4ff',
        quest: 'Allenamento Avatar Reale'
      }
    }

    return {
      text: 'Debuff attivi sul tuo sistema. Riorganizza le tue priorità, prenditi cura della tua stamina fisica e rimetti la tua quest principale al primo posto.',
      badge: 'Consiglio: Ricalibrazione',
      color: '#8b5cf6',
      quest: 'Inizia il tuo Respawn reale'
    }
  }

  const advice = getDynamicAdvice()

  return (
    <div className="max-w-4xl mx-auto w-full px-4 mb-12">
      <div 
        className="manga-panel rounded-2xl p-6 md:p-8 relative overflow-hidden"
        style={{
          border: '2px solid rgba(139,92,246,0.18)',
          background: 'linear-gradient(135deg, rgba(15,15,36,0.92), rgba(8,8,24,0.97))',
        }}
      >
        <div className="absolute top-4 left-4 w-6 h-6 border-l-2 border-t-2 border-nerd-cyan/20" />
        <div className="absolute top-4 right-4 w-6 h-6 border-r-2 border-t-2 border-nerd-cyan/20" />
        <div className="absolute bottom-4 left-4 w-6 h-6 border-l-2 border-b-2 border-nerd-cyan/20" />
        <div className="absolute bottom-4 right-4 w-6 h-6 border-r-2 border-b-2 border-nerd-cyan/20" />

        <div className="text-center mb-8 relative z-10">
          <div className="flex items-center justify-center gap-2 mb-1.5">
            <span className="w-2 h-2 rounded-full bg-nerd-purple animate-pulse" />
            <span className="text-[10px] text-nerd-purple font-display uppercase tracking-[0.25em]">Diagnostica Real Time</span>
          </div>
          <h3 className="font-display text-2xl md:text-3xl font-bold text-nerd-white">Simulatore di Statistiche dell'Avatar</h3>
          <p className="text-nerd-gray text-xs md:text-sm max-w-lg mx-auto mt-2 font-body">
            Seleziona i "debuff" (le cattive abitudini) che ti rallentano nella vita quotidiana. Guarda l'impatto reale sulle tue barre di HP, Mana e Stamina e scopri la quest di sblocco corretta.
          </p>
        </div>

        <div className="grid md:grid-cols-12 gap-8 relative z-10">
          
          {/* Colonna Sinistra — Selettore Debuff */}
          <div className="md:col-span-5 space-y-3">
            <span className="text-[9px] text-nerd-gray uppercase tracking-widest font-display block mb-1">Debuff Giornalieri Attivi:</span>
            
            {DEBUFFS_DATA.map((debuff) => {
              const isActive = selectedDebuffs.includes(debuff.id)
              return (
                <div
                  key={debuff.id}
                  onClick={() => toggleDebuff(debuff.id)}
                  className="rounded-xl p-3.5 cursor-pointer border select-none transition-all duration-300 flex items-start gap-3 relative group overflow-hidden"
                  style={{
                    background: isActive ? `${debuff.color}08` : 'rgba(5, 5, 20, 0.45)',
                    borderColor: isActive ? debuff.color : 'rgba(139,92,246,0.12)',
                    boxShadow: isActive ? `0 0 15px ${debuff.color}08` : 'none'
                  }}
                >
                  <div className="text-2xl shrink-0 mt-0.5">{debuff.icon}</div>
                  <div className="space-y-1 pr-6">
                    <div className="flex items-baseline gap-2">
                      <h4 className="font-display text-xs font-bold text-nerd-white group-hover:text-gradient transition-all">{debuff.name}</h4>
                      <span className="text-[7px] font-display uppercase tracking-wider px-1 py-0.5 rounded" style={{ color: debuff.color, background: `${debuff.color}15`, border: `1px solid ${debuff.color}25` }}>
                        {debuff.subtitle}
                      </span>
                    </div>
                    <p className="text-nerd-gray/80 text-[10px] leading-relaxed font-body">
                      {debuff.desc}
                    </p>
                  </div>

                  {/* Checkbox indicator */}
                  <div 
                    className="absolute top-3.5 right-3 w-4 h-4 rounded border flex items-center justify-center transition-colors"
                    style={{
                      borderColor: isActive ? debuff.color : 'rgba(139, 92, 246, 0.3)',
                      background: isActive ? debuff.color : 'transparent'
                    }}
                  >
                    {isActive && (
                      <svg viewBox="0 0 24 24" fill="none" stroke="#050510" strokeWidth="4" className="w-2.5 h-2.5">
                        <path d="M5 12l5 5L20 7" />
                      </svg>
                    )}
                  </div>
                </div>
              )
            })}
          </div>

          {/* Colonna Destra — Status dell'Avatar */}
          <div className="md:col-span-7 flex flex-col justify-between rounded-xl bg-nerd-dark/40 border border-nerd-purple/10 p-5 md:p-6">
            
            {/* Statistiche RPG */}
            <div className="space-y-4">
              <div className="flex items-center justify-between border-b border-nerd-purple/10 pb-2">
                <span className="text-[10px] text-nerd-gray uppercase tracking-wider font-display">Scheda Status dell'Avatar</span>
                <span 
                  className="px-2 py-0.5 rounded text-[8px] font-display font-semibold uppercase tracking-wider transition-all duration-300"
                  style={{
                    background: selectedDebuffs.length > 0 ? 'rgba(239,68,68,0.1)' : 'rgba(16,185,129,0.1)',
                    color: selectedDebuffs.length > 0 ? '#ef4444' : '#10b981',
                    border: selectedDebuffs.length > 0 ? '1px solid rgba(239,68,68,0.25)' : '1px solid rgba(16,185,129,0.25)'
                  }}
                >
                  {selectedDebuffs.length > 0 ? `${selectedDebuffs.length} Debuff Rilevati` : 'Avatar Purificato'}
                </span>
              </div>

              {/* Bar HP */}
              <div className="space-y-1">
                <div className="flex justify-between text-[10px] font-display uppercase">
                  <span className="text-nerd-red flex items-center gap-1">❤️ HP (Energia Fisica)</span>
                  <span className="font-bold text-nerd-white">{currentHp}%</span>
                </div>
                <div className="h-2.5 bg-nerd-dark rounded-full overflow-hidden border border-nerd-red/10">
                  <motion.div 
                    className="h-full rounded-full bg-gradient-to-r from-red-600 to-red-400"
                    animate={{ width: `${currentHp}%` }}
                    transition={{ type: 'spring', stiffness: 60 }}
                  />
                </div>
              </div>

              {/* Bar MANA */}
              <div className="space-y-1">
                <div className="flex justify-between text-[10px] font-display uppercase">
                  <span className="text-nerd-cyan flex items-center gap-1">🔷 MANA (Focus Mentale)</span>
                  <span className="font-bold text-nerd-white">{currentFocus}%</span>
                </div>
                <div className="h-2.5 bg-nerd-dark rounded-full overflow-hidden border border-nerd-cyan/10">
                  <motion.div 
                    className="h-full rounded-full bg-gradient-to-r from-nerd-cyan via-nerd-blue to-nerd-cyan"
                    animate={{ width: `${currentFocus}%` }}
                    transition={{ type: 'spring', stiffness: 60 }}
                  />
                </div>
              </div>

              {/* Bar STAMINA */}
              <div className="space-y-1">
                <div className="flex justify-between text-[10px] font-display uppercase">
                  <span className="text-nerd-purple flex items-center gap-1">⚡ STAMINA (Azione Reale)</span>
                  <span className="font-bold text-nerd-white">{currentStamina}%</span>
                </div>
                <div className="h-2.5 bg-nerd-dark rounded-full overflow-hidden border border-nerd-purple/10">
                  <motion.div 
                    className="h-full rounded-full bg-gradient-to-r from-nerd-purple via-[#a855f7] to-nerd-purple"
                    animate={{ width: `${currentStamina}%` }}
                    transition={{ type: 'spring', stiffness: 60 }}
                  />
                </div>
              </div>
            </div>

            {/* Diagnosi Dinamica */}
            <div 
              className="mt-6 p-4 rounded-lg border transition-all duration-500 relative overflow-hidden"
              style={{
                borderColor: `${advice.color}25`,
                background: `${advice.color}05`
              }}
            >
              <div className="absolute top-0 right-0 px-2 py-0.5 rounded-bl text-[8px] font-display font-semibold uppercase tracking-wider" style={{ color: advice.color, background: `${advice.color}15`, borderLeft: `1px solid ${advice.color}20`, borderBottom: `1px solid ${advice.color}20` }}>
                {advice.badge}
              </div>

              <div className="space-y-1.5 pt-1">
                <span className="text-[8px] text-nerd-gray uppercase font-display block tracking-widest">Diagnosi di Purificazione:</span>
                <p className="text-nerd-white text-xs leading-relaxed font-body">
                  {advice.text}
                </p>
                <div className="pt-2 flex items-center gap-2">
                  <span className="text-[8px] text-nerd-cyan uppercase font-display tracking-wider">Quest Consigliata:</span>
                  <span className="text-xs font-bold font-display" style={{ color: advice.color }}>{advice.quest}</span>
                </div>
              </div>
            </div>

          </div>
        </div>

      </div>
    </div>
  )
}
